/* ============================================================
   PROJECT AURUM — AHSAN TARIQ COUTURE
   Storefront runtime.

   Scope, deliberately narrow:
     1. Overlay services  — scroll lock, focus trap, focus restore
     2. The Bag           — drawer, add / remove / quantity
     3. Quantity steppers — shared by drawer and cart page
     4. Reveal on scroll  — one IntersectionObserver for the page
     5. Header metrics    — publishes header height as a CSS var

   No dependencies. Runs alongside the inherited jQuery stack
   rather than replacing it; the only legacy behaviour intercepted
   is the Add to Bag submit, which previously completed silently
   because the drawer it expected did not exist.

   Progressive enhancement: every form here still posts natively
   to Shopify if this file fails to load.
   ============================================================ */

(function () {
  'use strict';

  var CONFIG = window.AURUM || {};
  var ROUTES = window.routes || {};

  /* ==========================================================
     1. UTILITIES
     ========================================================== */

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function on(target, type, handler, options) {
    if (target) target.addEventListener(type, handler, options);
  }

  function prefersReducedMotion() {
    // matchMedia is absent in some in-app webviews; absence means
    // no stated preference, not a crash.
    if (typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  /**
   * Scroll lock with scrollbar compensation.
   *
   * Locking with `overflow:hidden` alone removes the scrollbar and
   * shifts the entire page sideways by its width. Publishing the
   * width as a custom property lets CSS pad it back out, so opening
   * a drawer no longer makes the page jump.
   *
   * Reference counted: NOVA and the bag can be open together
   * without one unlocking the other on close.
   */
  var scrollLock = (function () {
    var depth = 0;

    return {
      lock: function () {
        depth += 1;
        if (depth > 1) return;

        var gap = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty('--at-scrollbar-width', gap + 'px');
        document.body.classList.add('at-scroll-locked');
      },
      unlock: function () {
        depth = Math.max(0, depth - 1);
        if (depth > 0) return;

        document.body.classList.remove('at-scroll-locked');
        document.documentElement.style.removeProperty('--at-scrollbar-width');
      }
    };
  })();

  /**
   * Focus trap for modal surfaces.
   * Remembers the trigger so focus can be restored on close, which
   * is what keyboard and screen-reader users rely on to keep their
   * place in the page.
   */
  function createFocusTrap(container) {
    var previouslyFocused = null;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;

      var focusable = $$(FOCUSABLE, container).filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });

      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    return {
      activate: function () {
        previouslyFocused = document.activeElement;
        document.addEventListener('keydown', onKeydown, true);

        // Defer so the element is painted and focusable.
        window.requestAnimationFrame(function () {
          var target = container.querySelector('[data-autofocus]') || $$(FOCUSABLE, container)[0];
          if (target) target.focus({ preventScroll: true });
        });
      },
      release: function () {
        document.removeEventListener('keydown', onKeydown, true);

        if (previouslyFocused && typeof previouslyFocused.focus === 'function' && document.contains(previouslyFocused)) {
          previouslyFocused.focus({ preventScroll: true });
        }
        previouslyFocused = null;
      }
    };
  }

  function formatMoney(cents) {
    // The storefront money format is authoritative. Only the plain
    // amount is substituted; currency symbol and placement come
    // from Shopify so localisation stays correct.
    var format = CONFIG.moneyFormat || '{{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace(/\{\{\s*amount\s*\}\}/g, whole + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, whole + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole);
  }

  /* ==========================================================
     2. THE BAG
     ========================================================== */

  var CartDrawer = {
    el: null,
    trap: null,
    isOpen: false,

    init: function () {
      this.el = document.getElementById('CartDrawer');
      if (!this.el) return;

      this.trap = createFocusTrap(this.el);
      this.bind();
      this.syncCount();
    },

    bind: function () {
      var self = this;

      // Delegated so the handlers survive a section re-render.
      on(document, 'click', function (event) {
        var opener = event.target.closest('[data-cart-drawer-open]');
        if (opener) {
          event.preventDefault();
          self.open(opener);
          return;
        }

        if (event.target.closest('[data-cart-drawer-close]')) {
          event.preventDefault();
          self.close();
          return;
        }

        var remove = event.target.closest('[data-cart-remove]');
        if (remove && self.el.contains(remove)) {
          event.preventDefault();
          self.change(parseInt(remove.dataset.index, 10), 0);
        }
      });

      on(document, 'keydown', function (event) {
        if (event.key === 'Escape' && self.isOpen) {
          event.preventDefault();
          self.close();
        }
      });

      // Quantity edits inside the drawer, debounced so holding a
      // stepper does not fire a request per click.
      var debounceTimer = null;
      on(this.el, 'change', function (event) {
        var input = event.target.closest('[name="updates[]"]');
        if (!input) return;

        window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(function () {
          self.change(parseInt(input.dataset.index, 10), parseInt(input.value, 10));
        }, 320);
      });
    },

    open: function (trigger) {
      /*
       * AURUM architecture:
       * The cart engine remains here, but THE BAG is presented inside NOVA.
       * Do NOT open the legacy right-side CartDrawer or its backdrop.
       */
      var novaRoot = document.querySelector('[data-at-nova]');
      var novaLauncher = novaRoot && novaRoot.querySelector('[data-nova-open]');

      if (novaRoot && novaLauncher) {
        var novaIsOpen =
          novaRoot.getAttribute('data-open') === 'true' ||
          novaRoot.classList.contains('is-open');

        if (!novaIsOpen) {
          novaLauncher.click();
        }

        /*
         * NOVA must finish its open transition and mount THE BAG
         * before we activate the bag view.
         */
        window.setTimeout(function () {
          var novaBagButton = novaRoot.querySelector('[data-nova-bag-open]');
          if (novaBagButton) novaBagButton.click();
        }, 80);

        this.isOpen = false;

        document.dispatchEvent(
          new CustomEvent('aurum:cart:open', {
            detail: {
              trigger: trigger,
              surface: 'nova-bag'
            }
          })
        );

        return;
      }

      /* Safe fallback for any page where NOVA is not rendered. */
      if (!this.el || this.isOpen) return;

      this.isOpen = true;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');

      var backdrop = $('.at-backdrop', this.el);
      if (backdrop) backdrop.classList.add('is-open');

      scrollLock.lock();
      this.trap.activate();

      document.dispatchEvent(
        new CustomEvent('aurum:cart:open', { detail: { trigger: trigger, surface: 'legacy' } })
      );
    },

    close: function () {
      /*
       * When NOVA owns THE BAG there is no legacy drawer to close here.
       */
      if (!this.isOpen) return;

      this.isOpen = false;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');

      var backdrop = $('.at-backdrop', this.el);
      if (backdrop) backdrop.classList.remove('is-open');

      scrollLock.unlock();
      this.trap.release();

      document.dispatchEvent(new CustomEvent('aurum:cart:close'));
    },

    setBusy: function (line, busy) {
      if (!this.el) return;

      var item = line ? this.el.querySelector('#CartDrawer-Item-' + line) : null;
      var loader = item ? item.querySelector('[data-line-loading]') : null;

      if (loader) loader.hidden = !busy;
      this.el.classList.toggle('is-busy', !!busy);
    },

    announce: function (message) {
      var status = $('[data-cart-drawer-status]', this.el);
      if (status) status.textContent = message;
    },

    /**
     * Change a line quantity. Quantity 0 removes the line.
     * Uses the Section Rendering API so the drawer markup is
     * produced by Liquid rather than rebuilt in JavaScript —
     * prices, discounts and translations therefore stay correct.
     */
    change: function (line, quantity) {
      var self = this;
      if (!line || isNaN(quantity)) return;

      this.setBusy(line, true);

      var sections = ['cart-drawer', 'cart-icon-bubble'];
      var mainCartItems = document.getElementById('main-cart-items');
      var mainCartFooter = document.getElementById('main-cart-footer');

      // Only ask for the cart-page sections when we are on the cart
      // page. Requesting a section that is not rendered returns null
      // and the previous implementation then threw on it.
      if (mainCartItems && mainCartItems.dataset.id) sections.push(mainCartItems.dataset.id);

      fetch(ROUTES.cart_change_url || '/cart/change', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          sections: sections,
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (state) {
          if (state.status) {
            // Shopify returns { status, message, description } on error.
            self.showLineError(line, state.description || state.message);
            return;
          }

          self.render(state);
          self.syncCount(state.item_count);

          if (mainCartItems && mainCartFooter) self.renderCartPage(state, mainCartItems, mainCartFooter);

          self.announce(
            quantity === 0 ? 'Item removed from your bag.' : 'Bag updated. ' + state.item_count + ' items.'
          );

          document.dispatchEvent(new CustomEvent('aurum:cart:updated', { detail: state }));
        })
        .catch(function () {
          self.showLineError(line, (CONFIG.strings && CONFIG.strings.cartError) || 'Could not update your bag.');
        })
        .finally(function () {
          self.setBusy(line, false);
        });
    },

    showLineError: function (line, message) {
      var target = this.el && this.el.querySelector('#CartDrawer-LineError-' + line);
      if (target) target.textContent = message || '';
      this.announce(message || '');
    },

    /** Swap the drawer's inner markup with freshly rendered Liquid. */
    render: function (state) {
      if (!this.el || !state.sections || !state.sections['cart-drawer']) return;

      var parsed = new DOMParser().parseFromString(state.sections['cart-drawer'], 'text/html');
      var incoming = parsed.querySelector('.at-cart-drawer__inner');
      var current = $('.at-cart-drawer__inner', this.el);

      if (incoming && current) {
        current.innerHTML = incoming.innerHTML;
        this.el.classList.toggle('is-empty', state.item_count === 0);
      }

      if (state.sections['cart-icon-bubble']) {
        var bubbleSource = new DOMParser()
          .parseFromString(state.sections['cart-icon-bubble'], 'text/html')
          .querySelector('.shopify-section');
        var bubbleTarget = document.getElementById('cart-icon-bubble');
        if (bubbleSource && bubbleTarget) bubbleTarget.innerHTML = bubbleSource.innerHTML;
      }
    },

    /** Keep the /cart page in sync when the drawer is used there. */
    renderCartPage: function (state, itemsEl, footerEl) {
      var sectionId = itemsEl.dataset.id;
      var html = state.sections && state.sections[sectionId];
      if (!html) return;

      var parsed = new DOMParser().parseFromString(html, 'text/html');

      var newItems = parsed.querySelector('.js-contents');
      var currentItems = itemsEl.querySelector('.js-contents');
      if (newItems && currentItems) currentItems.innerHTML = newItems.innerHTML;

      var newFooter = parsed.querySelector('.js-contents1');
      var currentFooter = footerEl.querySelector('.js-contents1');
      if (newFooter && currentFooter) currentFooter.innerHTML = newFooter.innerHTML;

      itemsEl.classList.toggle('is-empty', state.item_count === 0);
      footerEl.classList.toggle('is-empty', state.item_count === 0);
    },

    /** Update every bag counter in the document. */
    syncCount: function (count) {
      if (typeof count !== 'number') return;

      $$('[data-cart-count], .at-header__cart-count, [data-nova-bag-count]').forEach(function (node) {
        node.textContent = count;
        node.hidden = count === 0;
      });

      $$('[data-cart-drawer-count]').forEach(function (node) {
        node.textContent = count > 0 ? '(' + count + ')' : '';
      });

      var header = $('.at-header');
      if (header) header.classList.toggle('has-cart-items', count > 0);
    }
  };

  /* ==========================================================
     3. ADD TO BAG

     Intercepted in the capture phase so it runs before the
     inherited jQuery delegate on <body>. The legacy path added the
     item correctly but gave no feedback under cart_type "drawer",
     which read to customers as "adding products does not work".
     ========================================================== */

  function bindNovaCartRouting() {
    document.addEventListener('click', function (event) {
      var target = event.target.closest(
        [
          '[data-cart-drawer-open]',
          '[data-cart-open]',
          '#cart-icon-bubble a',
          '#cart-icon-bubble button',
          '.at-header__cart a',
          '.at-header__cart button'
        ].join(',')
      );

      if (!target) return;
      if (target.closest('[data-at-nova]')) return;

      var novaRoot = document.querySelector('[data-at-nova]');
      var novaLauncher = novaRoot && novaRoot.querySelector('[data-nova-open]');

      if (!novaRoot || !novaLauncher) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      CartDrawer.open(target);
    }, true);
  }

  function bindAddToCart() {
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form.matches('[data-product-form], form[action*="/cart/add"]')) return;

        // Let the native POST through if the drawer is not in play.
        if (CONFIG.cartType !== 'drawer' || !document.getElementById('CartDrawer')) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        var button = form.querySelector('[type="submit"]');
        var formData = new FormData(form);

        formData.append('sections', 'cart-drawer,cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);

        if (button) {
          button.classList.add('is-adding');
          button.setAttribute('aria-busy', 'true');
          button.disabled = true;
        }

        fetch(ROUTES.cart_add_url || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (result) {
            if (result.status) {
              showFormError(form, result.description || result.message);
              return;
            }

            // Close any quickview / quickadd modal the add came from.
            closeLegacyModals();

            if (result.sections) {
              CartDrawer.render({ sections: result.sections, item_count: null });
            }

            return fetch((ROUTES.cart_url || '/cart') + '.js')
              .then(function (r) {
                return r.json();
              })
              .then(function (cart) {
                CartDrawer.syncCount(cart.item_count);
                CartDrawer.announce((CONFIG.strings && CONFIG.strings.addedToBag) || 'Added to bag');
                CartDrawer.open(button);
                document.dispatchEvent(new CustomEvent('aurum:cart:updated', { detail: cart }));
              });
          })
          .catch(function () {
            showFormError(form, (CONFIG.strings && CONFIG.strings.cartError) || 'Could not add to bag.');
          })
          .finally(function () {
            if (button) {
              button.classList.remove('is-adding');
              button.removeAttribute('aria-busy');
              button.disabled = false;
            }
          });
      },
      true // capture
    );
  }

  function showFormError(form, message) {
    var target = form.querySelector('[data-product-form-error]');

    if (!target) {
      target = document.createElement('p');
      target.className = 'at-form-error';
      target.setAttribute('role', 'alert');
      target.setAttribute('data-product-form-error', '');
      form.appendChild(target);
    }

    target.textContent = message || '';
  }

  function closeLegacyModals() {
    // Bootstrap modals are used by quickview / quickadd. Dismiss via
    // the library when present, otherwise fall back to the DOM so
    // the modal never strands on top of the drawer.
    var openModals = $$('.modal.show');

    openModals.forEach(function (modal) {
      if (window.bootstrap && window.bootstrap.Modal) {
        var instance = window.bootstrap.Modal.getInstance(modal);
        if (instance) {
          instance.hide();
          return;
        }
      }

      modal.classList.remove('show');
      modal.style.display = 'none';
      modal.setAttribute('aria-hidden', 'true');
    });

    $$('.modal-backdrop').forEach(function (backdrop) {
      backdrop.parentNode.removeChild(backdrop);
    });

    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('padding-right');
  }

  /* ==========================================================
     4. QUANTITY STEPPERS
     Delegated so re-rendered markup keeps working.
     ========================================================== */

  function bindQuantitySteppers() {
    on(document, 'click', function (event) {
      var button = event.target.closest('[data-qty-step]');
      if (!button) return;

      event.preventDefault();

      var wrapper = button.closest('[data-qty]');
      var input = wrapper && wrapper.querySelector('input');
      if (!input) return;

      var step = parseInt(button.dataset.qtyStep, 10) || 1;
      var min = input.min === '' ? 0 : parseInt(input.min, 10);
      var max = input.max === '' ? Infinity : parseInt(input.max, 10);
      var next = (parseInt(input.value, 10) || 0) + step;

      input.value = Math.min(max, Math.max(min, next));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ==========================================================
     5. REVEAL ON SCROLL
     One observer for the document. Elements unobserve after
     revealing so nothing is recomputed while scrolling.
     ========================================================== */

  function bindReveal() {
    var nodes = $$('[data-at-reveal]');
    if (!nodes.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      nodes.forEach(function (node) {
        node.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );

    nodes.forEach(function (node) {
      observer.observe(node);
    });
  }

  /* ==========================================================
     6. HEADER METRICS
     Publishes the real header height so sticky offsets and
     scroll-padding stay correct across breakpoints instead of
     being hard-coded per layout.
     ========================================================== */

  function bindHeaderMetrics() {
    var header = $('.at-header');
    if (!header) return;

    var publish = function () {
      var height = header.offsetHeight;
      document.documentElement.style.setProperty('--at-header-height', height + 'px');
      document.documentElement.style.setProperty('--at-header-offset', height + 16 + 'px');
    };

    publish();

    if ('ResizeObserver' in window) {
      new ResizeObserver(publish).observe(header);
    } else {
      on(window, 'resize', publish, { passive: true });
    }

    on(window, 'orientationchange', publish);
  }

  /* ==========================================================
     BOOT
     ========================================================== */

  function boot() {
    CartDrawer.init();
    bindNovaCartRouting();
    bindAddToCart();
    bindQuantitySteppers();
    bindReveal();
    bindHeaderMetrics();

    // Expose a minimal surface for NOVA and any theme-editor code.
    window.AURUM = Object.assign(window.AURUM || {}, {
      cart: CartDrawer,
      scrollLock: scrollLock,
      createFocusTrap: createFocusTrap,
      formatMoney: formatMoney
    });

    document.dispatchEvent(new CustomEvent('aurum:ready'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor: re-init after a section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('#CartDrawer')) CartDrawer.init();
    bindReveal();
    bindHeaderMetrics();
  });
})();
